import { Link } from "react-router-dom";
import { ArrowRight, ClipboardList, Wallet } from "lucide-react";
import { motion } from "framer-motion";
import { usePageMeta } from "../hooks/usePageMeta";
import { PageHeader } from "../components/PageHeader";
import { CtaBanner } from "../components/CtaBanner";
import { Reveal, fadeUp, stagger } from "../lib/motion";
import { CORSI_FORMAZIONE } from "../data/content";

export default function Formazione() {
  usePageMeta(
    "Formazione professionale",
    "Corsi di formazione professionale per sviluppare competenze spendibili nel mondo del lavoro: Centro Studi Forma Futuro."
  );

  return (
    <main>
      <PageHeader
        testId="page-formazione"
        eyebrow="Percorsi · Formazione professionale"
        title="Competenze concrete per il mondo del lavoro."
        description="I corsi di formazione professionale di Forma Futuro sono pensati per chi vuole crescere, riqualificarsi o acquisire nuove competenze."
      />
      <section className="pb-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <Reveal className="max-w-3xl mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-navy text-balance">
              I corsi disponibili
            </h2>
            <p className="mt-5 text-base text-slate-600 leading-relaxed">
              L'offerta formativa viene aggiornata nel tempo in base alle
              esigenze del territorio e del mercato del lavoro.
            </p>
          </Reveal>

          {CORSI_FORMAZIONE.length === 0 ? (
            <Reveal
              data-testid="formazione-empty-state"
              className="rounded-[1.6rem] border-2 border-dashed border-slate-300 bg-white/60 px-8 py-14 lg:py-16 text-center max-w-3xl"
            >
              <span className="mx-auto mb-5 inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-azure-light text-azure">
                <ClipboardList size={26} strokeWidth={1.8} />
              </span>
              <h3 className="text-xl font-bold text-navy">
                Elenco corsi in aggiornamento
              </h3>
              <p className="mt-3 text-sm sm:text-base text-slate-600 leading-relaxed max-w-lg mx-auto">
                L'elenco completo dei corsi di formazione professionale sarà
                disponibile a breve. Contattaci per conoscere le proposte
                attive e le prossime partenze.
              </p>
              <Link
                to="/contatti"
                data-testid="formazione-cta"
                className="mt-8 inline-flex items-center gap-2.5 rounded-full bg-azure px-7 py-3.5 text-sm font-bold text-white transition-all duration-300 hover:bg-[#1C446E] hover:-translate-y-0.5 group"
              >
                Contattaci per l'elenco aggiornato
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          ) : (
            <motion.div
              variants={stagger}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-60px" }}
              className="grid grid-cols-1 lg:grid-cols-2 gap-7"
            >
              {CORSI_FORMAZIONE.map((c, i) => (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  data-testid={`corso-formazione-${i}`}
                  className="bg-white rounded-[1.6rem] border border-slate-200 p-8 lg:p-9 flex flex-col transition-all duration-300 hover:-translate-y-1 hover:border-azure hover:shadow-[0_28px_60px_-30px_rgba(8,42,89,0.35)]"
                >
                  <div className="flex items-start justify-between gap-5">
                    <h3 className="text-xl lg:text-2xl font-extrabold tracking-tight text-navy leading-snug">
                      {c.titolo}
                    </h3>
                    <span className="shrink-0 rounded-full bg-navy text-white px-4 py-2 text-sm font-bold">
                      {c.prezzo}
                    </span>
                  </div>
                  <p className="mt-3 text-sm sm:text-base text-slate-600 leading-relaxed">
                    {c.intro}
                  </p>
                  <dl className="mt-6 space-y-3 flex-1">
                    {c.dettagli.map((d) => (
                      <div key={d.label} className="flex items-start gap-4">
                        <dt className="w-32 shrink-0 pt-0.5 text-[11px] font-bold uppercase tracking-[0.12em] text-slate-400">
                          {d.label}
                        </dt>
                        <dd className="text-sm text-slate-700 leading-relaxed">
                          {d.value}
                        </dd>
                      </div>
                    ))}
                  </dl>
                  <div className="mt-7 pt-6 border-t border-slate-100 flex flex-wrap items-center justify-between gap-4">
                    <span className="inline-flex items-center gap-2.5 rounded-full bg-azure-light border border-azure-soft px-4 py-2 text-xs font-bold text-navy">
                      <Wallet size={14} className="text-azure shrink-0" />
                      Pagamento a rate disponibile
                    </span>
                    <Link
                      to="/contatti"
                      data-testid={`corso-cta-${i}`}
                      className="inline-flex items-center gap-2 text-sm font-bold text-azure group"
                    >
                      Richiedi informazioni
                      <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </section>
      <CtaBanner
        title="Vuoi formarti per il lavoro?"
        text="Raccontaci che competenze vuoi sviluppare: ti orientiamo tra i corsi di formazione professionale disponibili."
      />
    </main>
  );
}
